import { gql } from "@apollo/client";

export class GraphQLQuery {
  static signIn(email: string, password: string) {
    return gql`
      mutation MyMutation {
        signIn(email: "${email}", password: "${password}") {
          _id
          email
          name
          picture
          searchable
          data
          token
        }
      }
    `;
  }

  static signUp(name: string, email: string, password: string) {
    return gql`
      mutation MyMutation {
        signUp(name: "${name}", email: "${email}", password: "${password}") {
          _id
          email
          name
          picture
          searchable
          data
          token
        }
      }
    `;
  }

  static changeName(newName: string) {
    return gql`
      mutation MyMutation {
        changeName(newName: "${newName}") {
          _id
          email
          name
          picture
        }
      }
    `;
  }

  static changePassword(oldPassword: string, newPassword: string) {
    return gql`
      mutation MyMutation {
        changePassword(oldPassword: "${oldPassword}", newPassword: "${newPassword}") {
          _id
          email
          name
          picture
        }
      }
    `;
  }

  static updateUserData() {
    return gql`
      mutation UpdateUserData($newData: JSON!) {
        updateUserData(newData: $newData) {
          _id
          data
        }
      }
    `;
  }

  static updateUserDataField() {
    return gql`
      mutation UpdateUserDataField($newData: JSON!) {
        updateUserDataField(newData: $newData) {
          _id
          data
        }
      }
    `;
  }

  static searchUser(search: string) {
    return gql`
      query MyQuery {
        allUsers(search: "${search}") {
          _id
          name
          email
          picture
          data
        }
      }
    `;
  }

  static getMyInfo = gql`
    query MyQuery {
      getMyInfo {
        _id
        name
        email
        picture
        data
      }
    }
  `;

  static sendFriendRequest(userId: string) {
    return gql`
      mutation MyMutation {
        sendFriendRequest(recipientId: "${userId}") {
          _id
          createdAt
          recipient {
            _id
          }
          requester {
            _id
          }
          status
        }
      }
    `;
  }

  static cancelFriendRequest(friendshipId: string) {
    return gql`
      mutation MyMutation {
        cancelFriendRequest(friendshipId: "${friendshipId}") {
          _id
          updatedAt
          recipient {
            _id
          }
          requester {
            _id
          }
          status
        }
      }
    `;
  }

  static acceptFriendRequest(friendshipId: string) {
    return gql`
      mutation MyMutation {
        acceptFriendRequest(friendshipId: "${friendshipId}") {
          _id
          updatedAt
          recipient {
            _id
          }
          requester {
            _id
          }
          status
        }
      }
    `;
  }

  static rejectFriendRequest(friendshipId: string) {
    return gql`
      mutation MyMutation {
        rejectFriendRequest(friendshipId: "${friendshipId}") {
          _id
          updatedAt
          recipient {
            _id
          }
          requester {
            _id
          }
          status
        }
      }
    `;
  }

  static sentFriendRequests = gql`
    query MyQuery {
      sentFriendRequests {
        _id
        createdAt
        recipient {
          _id
          email
          name
          picture
        }
        requester {
          _id
          email
          name
          picture
        }
        status
      }
    }
  `;

  static receivedFriendRequests = gql`
    query MyQuery {
      receivedFriendRequests {
        _id
        createdAt
        recipient {
          _id
          email
          name
          picture
        }
        requester {
          _id
          email
          name
          picture
        }
        status
      }
    }
  `;

  static fetchChats = gql`
    query MyQuery {
      fetchChats {
        _id
        chatName
        groupAdmin {
          _id
          email
          name
          picture
          data
        }
        latestMessage {
          type
          content
          createdAt
          _id
          sender {
            _id
            email
            name
            picture
            data
          }
          updatedAt
        }
        isGroupChat
        users {
          _id
          email
          name
          picture
          data
        }
      }
    }
  `;

  static getFriends = gql`
    query MyQuery {
      getFriends {
        _id
        name
        email
        picture
        isOnline
        lastSeen
      }
    }
  `;

  static deleteChat(chatId: string) {
    return gql`
      mutation MyMutation {
        deleteChat(chatId: "${chatId}") {
          _id
          users {
            _id
          }
        }
      }
    `;
  }

  static createGroupChat(name: string, users: string[]) {
    return gql`
      mutation MyMutation {
        createGroupChat(name: "${name}", users: ${JSON.stringify(users)}) {
          chatName
          groupAdmin {
            _id
            email
            name
            picture
            data
          }
          users {
            _id
            email
            name
            picture
            data
          }
          latestMessage {
            _id
          }
          createdAt
          updatedAt
        }
      }
    `;
  }

  static updateGroupChat(
    chatId: string,
    name: string,
    admin: string,
    users: string[],
  ) {
    return gql`
      mutation MyMutation {
        updateGroupChat(chatId: "${chatId}", groupAdmin: "${admin}", name: "${name}", users: ${JSON.stringify(users)}) {
          _id
          chatName
          groupAdmin {
            _id
            email
            name
            picture
            data
          }          
          users {
            _id
            email
            name
            picture
            data
          }
          latestMessage {
            _id
          }
          createdAt
          updatedAt
        }
      }
    `;
  }

  static deleteGroupChat(chatId: string) {
    return gql`
      mutation MyMutation {
        deleteGroupChat(chatId: "${chatId}") {
          _id
          users {
            _id
          }
        }
      }
    `;
  }

  static removeFromGroup(chatId: string, userId: string) {
    return gql`
      mutation MyMutation {
        removeFromGroup(chatId: "${chatId}", userId: "${userId}") {
          _id
          chatName
          createdAt
          updatedAt
          groupAdmin {
            _id
          }
          users {
            _id
          }
        }
      }
    `;
  }

  static allMessages(
    chatId: string,
    messageId?: string,
    endMessageId?: string,
    count: number = 50,
  ) {
    return gql`
      query MyQuery {
        allMessages(
          chatId: "${chatId}"
          count: ${count}
          ${messageId ? `messageId: "${messageId}"` : ""}
          ${endMessageId ? `endMessageId: "${endMessageId}"` : ""}
        ) {
          _id
          type
          content
          createdAt
          sender {
            _id
          }
          updatedAt
        }
      }
    `;
  }

  static updateMessage(messageId: string, content: string) {
    return gql`
      mutation UpdateMessage {
        updateMessage(messageId: "${messageId}", content: "${content}") {
          _id
          type
          content
          createdAt
          sender {
            _id
          }
          updatedAt
        }
      }
    `;
  }
}
